<template>
  <v-container fluid>
    <content-header
      header-icon="mdi-cash-multiple"
      header-title="Price Tiers"
      back-navigation
      back-navigation-href="/admin"
      back-navigation-text="To Old Admin"
    >
    </content-header>
    <v-skeleton-loader
      v-if="loading"
      type="article, actions"
    ></v-skeleton-loader>
    <pt-sheet v-else>
      <div class="mb-4">
        <v-alert
          v-if="!loading && selectedVendor && !editMode"
          :type="isDefaultPriceTiers ? 'info' : 'warning'"
        >
          This vendor ({{ selectedVendor.name }}) is using
          {{ isDefaultPriceTiers ? "Default" : "Custom" }} Price Tiers.
        </v-alert>
      </div>
      <div v-if="!editMode">
        <div class="price-tier-grid body-desktop-medium-regular-16">
          <div
            v-for="(tier, index) in priceTiers"
            :key="tier.id"
            class="price-tier-row"
          >
            <div class="body-desktop-large-bold-18 pr-4">
              Tier {{ index + 1 }}:
            </div>
            <div>{{ tier.minEmployees }}</div>
            <div>-</div>
            <div>
              {{ tier.maxEmployees === 2147483647 ? "∞" : tier.maxEmployees }}
            </div>
            <div class="pr-2">employees:</div>
            <div>{{ tier.price > 0 ? `$${tier.price}` : "" }}</div>
            <div>
              {{ tier.pricePerEmployee > 0 && tier.price > 0 ? "+" : "" }}
            </div>
            <div>
              {{
                tier.pricePerEmployee > 0
                  ? `$${tier.pricePerEmployee.toFixed(2)}`
                  : ""
              }}
            </div>
            <div>
              {{
                tier.pricePerEmployee > 0
                  ? `per employee over ${tier.minEmployees - 1}`
                  : ""
              }}
            </div>
            <div>
              <!--Intentionally empty so column count matches edit mode-->
            </div>
          </div>
        </div>
        <div class="text-right mt-4">
          <v-btn color="primary" @click="editMode = true" class="mr-2"
            >Edit Price Tiers</v-btn
          >
          <v-btn
            v-if="!isDefaultPriceTiers"
            color="primary"
            @click="showResetConfirm = true"
            >Reset to Default</v-btn
          >
          <confirm-dialog
            v-model="showResetConfirm"
            title="Reset Confirmation"
            message="Are you sure you want to reset this vendor's price tiers to the default?"
            @confirm="resetToDefault"
          ></confirm-dialog>
        </div>
      </div>
      <div v-else>
        <div class="price-tier-grid body-desktop-medium-regular-16">
          <div
            v-for="(tier, index) in priceTiers"
            :key="tier.id"
            class="price-tier-row"
          >
            <div class="body-desktop-large-bold-18 pr-4">
              Tier {{ index + 1 }}:
            </div>
            <div>
              <span>{{ index === 0 ? "0" : "" }}</span>
              <v-text-field
                v-if="index !== 0"
                v-model.number="tier.minEmployees"
                @input="updatePreviousMaxEmployees(index)"
                type="number"
                dense
                outlined
                class="inline-text-field"
                hide-details
              ></v-text-field>
            </div>
            <div>-</div>
            <div>
              <v-text-field
                v-if="index !== priceTiers.length - 1"
                v-model.number="tier.maxEmployees"
                @input="updateNextMinEmployees(index)"
                type="number"
                dense
                outlined
                class="inline-text-field"
                hide-details
              ></v-text-field>
              <span v-else>∞</span>
            </div>
            <div class="pr-2">employees:</div>
            <div>
              <span class="pr-1">$</span>
              <v-text-field
                v-model.number="tier.price"
                type="number"
                dense
                outlined
                class="inline-text-field"
                hide-details
              ></v-text-field>
            </div>
            <div>+</div>
            <div>
              <span class="pr-1">$</span>
              <v-text-field
                v-model.number="tier.pricePerEmployee"
                type="number"
                dense
                outlined
                class="inline-text-field"
                hide-details
              ></v-text-field>
            </div>
            <div>
              <span>per employee over {{ tier.minEmployees - 1 }}</span>
            </div>
            <v-btn icon @click="deletePriceTier(index)">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="text-right mt-4">
          <v-btn color="primary" @click="showSaveConfirm = true" class="mr-2"
            >Save Price Tiers
          </v-btn>
          <confirm-dialog
            v-model="showSaveConfirm"
            title="Save Confirmation"
            message="Saving these price tiers will immediately update the vendor's subscription amount.
              Are you sure you want to proceed?"
            @confirm="savePriceTiers"
          ></confirm-dialog>
          <v-btn color="primary" @click="insertPriceTier" class="mr-2"
            >Insert Price Tier
          </v-btn>
          <v-btn color="primary" @click="cancelEdit">Cancel</v-btn>
        </div>
      </div>
    </pt-sheet>
  </v-container>
</template>

<script>
import { get, post } from "@/services/request";
import { currentVendorAPI, employeeMonitorPriceAPI } from "@/services/api";
import PtSheet from "@/components/PtSheet.vue";
import ContentHeader from "@/views/ContentHeader.vue";
import hasNotifications from "@/mixins/has-notifications";
import ConfirmDialog from "@/components/ConfirmDialog.vue";

export default {
  name: "PriceTiers",
  components: { ContentHeader, PtSheet, ConfirmDialog },
  mixins: [hasNotifications],
  data: () => ({
    priceTiers: [],
    originalPriceTiers: [],
    loading: false,
    editMode: false,
    selectedVendor: null,
    showResetConfirm: false,
    showSaveConfirm: false
  }),
  computed: {
    isDefaultPriceTiers() {
      return !this.priceTiers.some(tier => tier.vendorId !== null);
    }
  },
  created() {
    this.loadPriceTiers();
    this.getCurrentVendor();
  },
  watch: {
    editMode(newValue) {
      if (newValue) {
        this.originalPriceTiers = JSON.parse(JSON.stringify(this.priceTiers));
      }
    }
  },
  methods: {
    loadPriceTiers() {
      this.loading = true;
      get(employeeMonitorPriceAPI)
        .then(response => {
          if (response && response.data) {
            this.priceTiers =
              response.data.map(tier => {
                if (tier.vendorId === null) {
                  tier.id = null; // Force new tier to be created on save if editing a default tier
                }
                return tier;
              }) || [];
          }
          this.loading = false;
        })
        .catch(() => {
          this.showMessage({
            text: "Unable to load price tiers",
            type: "error"
          });
          this.loading = false;
        });
    },
    getCurrentVendor() {
      get(currentVendorAPI)
        .then(response => {
          if (response && response.data) {
            this.selectedVendor = response.data;
          }
        })
        .catch(() => {
          this.showMessage({
            text: "Unable to load current vendor",
            mode: "error"
          });
          return Promise.reject();
        });
    },
    savePriceTiers() {
      this.loading = true;
      post(employeeMonitorPriceAPI, this.priceTiers)
        .then(() => {
          this.showMessage({
            text: "Price Tiers successfully saved.",
            mode: "success"
          });
          this.editMode = false;
          this.loadPriceTiers();
          this.showSaveConfirm = false;
        })
        .catch(() => {
          this.showMessage({
            text: "Error saving Price Tiers.",
            mode: "error"
          });
        });
    },
    cancelEdit() {
      this.priceTiers = JSON.parse(JSON.stringify(this.originalPriceTiers));
      this.editMode = false;
    },
    insertPriceTier() {
      const lastTier =
        this.priceTiers.length > 0
          ? this.priceTiers[this.priceTiers.length - 1]
          : undefined;
      const secondToLastTier =
        this.priceTiers.length > 1
          ? this.priceTiers[this.priceTiers.length - 2]
          : undefined;
      const newTier = {
        id: null,
        minEmployees: secondToLastTier ? secondToLastTier.maxEmployees + 1 : 0,
        maxEmployees: secondToLastTier
          ? secondToLastTier.maxEmployees + 100
          : lastTier
          ? 100
          : 2147483647,
        price: 0,
        pricePerEmployee: 0,
        vendorId: null
      };
      if (lastTier) {
        this.priceTiers.splice(this.priceTiers.length - 1, 0, newTier);
        lastTier.minEmployees = newTier.maxEmployees + 1;
      } else {
        this.priceTiers.push(newTier);
      }
    },
    updateNextMinEmployees(index) {
      if (index < this.priceTiers.length - 1) {
        this.priceTiers[index + 1].minEmployees =
          this.priceTiers[index].maxEmployees + 1;
      }
    },
    updatePreviousMaxEmployees(index) {
      if (index > 0) {
        this.priceTiers[index - 1].maxEmployees =
          this.priceTiers[index].minEmployees - 1;
      }
    },
    deletePriceTier(index) {
      if (this.priceTiers.length === 1) {
        this.priceTiers = [];
      } else {
        if (index === 0) {
          this.priceTiers[1].minEmployees = 0;
        } else if (index === this.priceTiers.length - 1) {
          this.priceTiers[this.priceTiers.length - 2].maxEmployees = 2147483647;
        } else {
          this.priceTiers[index + 1].minEmployees = this.priceTiers[
            index
          ].minEmployees;
        }
        this.priceTiers.splice(index, 1);
      }
    },
    resetToDefault() {
      this.priceTiers = [];
      this.savePriceTiers();
      this.showResetConfirm = false;
    }
  }
};
</script>

<style scoped>
.inline-text-field {
  display: inline-block;
  width: 100px;
  vertical-align: middle;
}

.price-tier-grid {
  display: grid;
  grid-template-columns: repeat(10, auto);
  gap: 7px;
  align-items: center;
  max-width: fit-content;
  margin: 0 auto;
}

.price-tier-row {
  display: contents;
}

.price-tier-row > div {
  display: flex;
  align-items: center;
}
</style>
